import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const MoreMenu = ({ open, onClose, items }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="fixed inset-0 z-[60]" data-testid="more-menu">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 animate-[fadeIn_200ms_ease-out]"
        onClick={onClose}
      />

      {/* Panel — slide up + fade in */}
      <div
        className={`absolute bottom-0 left-0 right-0 rounded-t-2xl pb-8 pt-3 px-4 animate-[slideUp_250ms_ease-out] ${
          isDark ? 'bg-[#333333]' : 'bg-white'
        }`}
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Handle */}
        <div className="flex justify-center mb-3">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-[#3A3A3A]' : 'bg-gray-300'}`} />
        </div>

        {/* Close */}
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>
            Plus
          </h3>
          <button
            onClick={onClose}
            className={`w-9 h-9 rounded-full flex items-center justify-center ${isDark ? 'bg-[#444444]' : 'bg-gray-100'}`}
          >
            <X size={18} className={isDark ? 'text-gray-400' : 'text-gray-600'} />
          </button>
        </div>

        {/* Items grid */}
        <div className="grid grid-cols-4 gap-4">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  onClose();
                }}
                className="flex flex-col items-center gap-2 py-3"
                data-testid={`more-menu-${item.id}`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    active
                      ? 'bg-[#9398DE]/15 text-[#9398DE]'
                      : isDark
                        ? 'bg-[#444444] text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                </div>
                <span
                  className={`text-xs text-center leading-tight ${
                    active
                      ? 'font-semibold text-[#9398DE]'
                      : isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MoreMenu;
